import { parseEther } from 'ethers'
import { useEffect, useState } from 'react'

import { useConnectModal } from '@rainbow-me/rainbowkit'
import { formatEther, formatUnits } from 'viem'
import { avalanche } from 'viem/chains'
import { useAccount, useBalance, useWatchAsset } from 'wagmi'

import IconInfo from '../IconInfo/IconInfo'
import { StakeForm } from '../LiquidStaking'
import RequestRedemption from '../RequestRedemption/RequestRedemption'
import StakingToggle from './StakingToggle/StakingToggle'
import ApproveAVAXButton from '../Wizard/components/ApproveAVAXButton'

import { Tooltip } from '@/common/components/Tooltip'
import { useValueTokens } from '@/contexts/VaultTokensContext'
import { Panel, usd } from '@/design-system/ui'
import {
  useBalanceOf,
  useConvertToAssets,
  useConvertToShares,
  useMaxDeposit,
  useMaxRedeem,
} from '@/hooks/useAvaxBalanceOfggAvax'
import { useCoinGeckoPrices } from '@/hooks/useCoinGeckoPrices'
import {
  usePendingRewards,
  useUnderlyingBalance,
  useVaultRatio,
  useVaultTotalAssets,
} from '@/hooks/useGetVaultData'
import { useRewards } from '@/hooks/useRewards'
import { formatNumber } from '@/utils/numberFormatter'

const STRATEGY_TOOLTIP =
  'The vault puts deposited AVAX to work in a principal-protected yield strategy on Avalanche, aiming to earn yield while protecting your deposited AVAX. Returns accrue to the seaAVAX share price, and you can redeem whenever the pool has liquidity.'

function MiniStat({
  accent = false,
  label,
  symbol,
  value,
}: {
  accent?: boolean
  label: string
  symbol: string
  value: string
}) {
  return (
    <div
      style={{
        background: 'var(--surface-quiet)',
        borderRadius: 'var(--radius-control)',
        padding: '16px 16px',
      }}
    >
      <div className="rng-labelcaps">{label}</div>
      <div
        className="mt-2 tabular-nums"
        style={{
          color: accent ? 'var(--accent-strong)' : 'var(--ink)',
          fontSize: 16,
          fontWeight: 400,
          letterSpacing: 0,
        }}
      >
        {value} <span style={{ color: 'var(--muted)', fontSize: 12 }}>{symbol}</span>
      </div>
    </div>
  )
}

function QuoteRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3" style={{ fontSize: 12 }}>
      <span style={{ color: 'var(--muted)' }}>{label}</span>
      <span className="tabular-nums" style={{ color: 'var(--ink)' }}>
        {value}
      </span>
    </div>
  )
}

export default function StakingForm() {
  const { chain, address: account, isConnected } = useAccount()
  const { openConnectModal } = useConnectModal()
  const { currentToken } = useValueTokens()
  const { data: vaultTotalAssets } = useVaultTotalAssets()
  const { data: maxDeposit } = useMaxDeposit()
  const { data: maxRedeem } = useMaxRedeem()
  const { data: pendingRewards } = usePendingRewards()
  const { data: availableLiquidity } = useUnderlyingBalance()
  const { watchAsset } = useWatchAsset()
  const { data: ratio } = useVaultRatio()
  const ratioDecimal = ratio && parseFloat(formatUnits(ratio, 18))
  const [amount, setAmount] = useState<bigint>(0n)
  const [toggle, setToggle] = useState<'stake' | 'unstake'>('stake')
  const [amountGreaterThanCap, setAmountGreaterThanCap] = useState(false)
  const [amountGreaterThanPool, setAmountGreaterThanPool] = useState(false)

  const GGPAddress = currentToken.tokenAddresses[0][chain?.id || avalanche.id] as `0x${string}`
  const xGGPAddress = currentToken.tokenAddresses[1][chain?.id || avalanche.id] as `0x${string}`

  const { data: balance } = useBalance({
    address: account,
    token: GGPAddress,
    query: {
      refetchInterval: 5000,
    },
  })

  const { data: ggAVAXBalance } = useBalance({
    address: account,
    token: xGGPAddress,
    query: {
      refetchInterval: 5000,
    },
  })

  const { data: balanceOf } = useBalanceOf()

  const { data: convertToAssetsData } = useConvertToAssets(balanceOf || 0n)
  const { data: maxRedeemToAssetsData } = useConvertToAssets(maxRedeem)
  const { data: availableLiquidityShares } = useConvertToShares(availableLiquidity || 0n)
  const { data: safeAvailableLiquidity } = useConvertToAssets(availableLiquidityShares || 0n)

  const { rewards } = useRewards(account, ratioDecimal, ggAVAXBalance?.value || 0n)

  const additional =
    vaultTotalAssets && pendingRewards && ggAVAXBalance?.value
      ? (BigInt(ggAVAXBalance?.value) * BigInt(pendingRewards)) / BigInt(vaultTotalAssets)
      : 0n

  const rewardsToShow = formatEther(BigInt(rewards.toFixed(0)) + additional || 0n)
  const positionToShow = convertToAssetsData ? formatEther(convertToAssetsData) : '0'

  // Live quote for the action ticket. On deposit we preview the seaAVAX shares minted for the
  // entered AVAX; the estimated USD value uses the same CoinGecko price key the rest of the app
  // reads. Share price comes from the vault ratio already read above.
  const { data: sharesQuote } = useConvertToShares(toggle === 'stake' ? amount : 0n)
  const { prices } = useCoinGeckoPrices()
  const priceKey = currentToken.value === 'ggp' ? 'gogopool' : 'avalanche-2'
  const usdPrice = prices?.[priceKey]?.usd
  const amountNum = Number(formatEther(amount))
  const estValueUsd = usdPrice != null ? amountNum * usdPrice : undefined
  const sharesReceived = sharesQuote != null ? formatEther(sharesQuote) : undefined

  const clearAmount = () => {
    setAmount(0n)
  }

  useEffect(() => {
    if (maxDeposit) {
      setAmountGreaterThanCap(amount > maxDeposit)
    } else {
      setAmountGreaterThanCap(amount > 0n)
    }
  }, [amount, maxDeposit])

  useEffect(() => {
    if (maxRedeemToAssetsData) {
      setAmountGreaterThanPool(amount > maxRedeemToAssetsData)
    } else {
      setAmountGreaterThanPool(amount > 0n)
    }
  }, [amount, maxRedeemToAssetsData])

  return (
    <Panel className="w-full">
      <div className="mb-5 flex items-center justify-between gap-3">
        <h2 style={{ fontSize: 19, fontWeight: 400, letterSpacing: 0 }}>Deposit &amp; withdraw</h2>
        <Tooltip content={STRATEGY_TOOLTIP} placement="left">
          <span style={{ color: 'var(--faint)' }}>
            <IconInfo className="size-5" />
          </span>
        </Tooltip>
      </div>

      <StakingToggle onChange={setToggle} />

      <div
        className="mt-5 p-5"
        style={{ background: 'var(--surface-quiet)', borderRadius: 'var(--radius-card)' }}
      >
        {toggle === 'stake' ? (
          <StakeForm
            amount={amount}
            balance={balance?.value || 0n}
            greaterThanPool={amountGreaterThanCap}
            header="Amount to deposit"
            max={maxDeposit}
            setAmount={setAmount}
            toggle={toggle}
            token={`${currentToken.displayName}`}
          />
        ) : (
          <StakeForm
            amount={amount}
            balance={convertToAssetsData || parseEther('0')}
            greaterThanPool={amountGreaterThanPool}
            header="Amount to withdraw"
            max={maxRedeemToAssetsData}
            setAmount={setAmount}
            toggle={toggle}
            token={`${currentToken.displayName}`}
          />
        )}
      </div>

      {/* Live quote — what you get before you confirm */}
      {amount > 0n && (
        <div
          className="mt-3 grid gap-2 p-3"
          style={{ background: 'var(--surface-quiet)', borderRadius: 'var(--radius-control)' }}
        >
          {toggle === 'stake' ? (
            <QuoteRow
              label={`sea${currentToken.displayName} received ≈`}
              value={
                sharesReceived
                  ? `${formatNumber(sharesReceived, 4)} sea${currentToken.displayName}`
                  : '—'
              }
            />
          ) : (
            <QuoteRow
              label={`${currentToken.displayName} received ≈`}
              value={`${formatNumber(formatEther(amount), 4)} ${currentToken.displayName}`}
            />
          )}
          <QuoteRow
            label="Estimated value"
            value={estValueUsd !== undefined ? usd(estValueUsd) : '—'}
          />
          {ratioDecimal ? (
            <QuoteRow
              label="Share price"
              value={`1 sea${currentToken.displayName} = ${formatNumber(String(ratioDecimal), 4)} ${currentToken.displayName}`}
            />
          ) : null}
        </div>
      )}

      {/* Your position / rewards — a summary just before the action (matches the reference) */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <MiniStat
          accent
          label="Your position"
          symbol={currentToken.displayName}
          value={isConnected ? formatNumber(positionToShow, 4) : '—'}
        />
        <MiniStat
          label="Your rewards"
          symbol={currentToken.displayName}
          value={isConnected ? formatNumber(rewardsToShow || '0', 6) : '—'}
        />
      </div>

      <div className="mt-5 flex flex-col items-center gap-3">
        <div className="w-full" style={{ minHeight: 56 }}>
          {isConnected ? (
            <ApproveAVAXButton
              amount={amount}
              amountGreaterThanCap={amountGreaterThanCap}
              amountGreaterThanPool={amountGreaterThanPool}
              balance={toggle === 'stake' ? balance?.value || 0n : convertToAssetsData || 0n}
              clearAmount={clearAmount}
              maxDeposit={maxDeposit}
              swapDirection={toggle === 'unstake'}
            />
          ) : (
            <button className="rng-btn rng-btn--ink" onClick={openConnectModal} type="button">
              Connect wallet to {toggle === 'stake' ? 'deposit' : 'withdraw'}
            </button>
          )}
        </div>

        {isConnected && toggle === 'unstake' && (
          <>
            <RequestRedemption
              availableLiquidity={safeAvailableLiquidity}
              defaultAmount={amount > 0n ? formatEther(amount) : ''}
              emphasize={amountGreaterThanPool}
              token={currentToken.displayName}
              triggerLabel={
                amountGreaterThanPool ? 'Request redemption instead' : 'Request redemption'
              }
            />
            <p
              style={{ color: 'var(--muted)', fontSize: 12, lineHeight: 1.5, textAlign: 'center' }}
            >
              {amountGreaterThanPool ? (
                <>
                  <b>Not enough liquidity to redeem this now.</b> Request a redemption and we re-add
                  liquidity within 24 hours, then come back to this form to finish.
                </>
              ) : (
                <>Short on liquidity? Request a redemption and we re-add it within 24 hours.</>
              )}
            </p>
          </>
        )}

        {isConnected ? (
          <button
            className="pt-1 transition-colors hover:text-[color:var(--ink)]"
            onClick={() => {
              watchAsset({
                type: 'ERC20',
                options: {
                  address: xGGPAddress,
                  symbol: `sea${currentToken.displayName}`,
                  decimals: 18,
                },
              })
            }}
            style={{ color: 'var(--muted)', fontSize: 13 }}
            type="button"
          >
            Add sea{currentToken.displayName} to wallet
          </button>
        ) : (
          <p style={{ color: 'var(--muted)', fontSize: 13, textAlign: 'center' }}>
            Live vault stats above update without a wallet.
          </p>
        )}
      </div>
    </Panel>
  )
}
